import React, { forwardRef } from 'react';
import { ShortNovelasClipsUiModuleBody } from '../../../../../API';
import { UIModuleTypeForm } from '../../UIModuleForm';
import { Controller, useFormContext } from 'react-hook-form';
import { useLocales } from '../../../../../hooks';
import { FormBody, FormField } from '../../styles';
import { Box } from '@mui/material';
import { AssetTypes, AssetTypesEnum } from '../../../../../utils/assetTypes';
import { markAsRequired } from '../../../../../utils/formHelpers';
import VideoAssetBrowser from '../../../../Assets/VideoAssetBrowser';
import { LokaliseField } from '../../../../shared/LokaliseFields';
import { shortNovelasClipsFormTestIds } from '../../../../shared/TestsIds';

const ShortNovelasClipsForm = forwardRef<UIModuleTypeForm<ShortNovelasClipsUiModuleBody>>(() => {
  const { control, watch } = useFormContext<ShortNovelasClipsUiModuleBody>();
  const { t } = useLocales();

  const moduleId = watch('moduleId');

  type ShortNovelasClipsProps = keyof ShortNovelasClipsUiModuleBody;

  const VideoAssetBrowserField = ({
    name,
    label,
    assetType,
    required = false
  }: {
    name: ShortNovelasClipsProps;
    label: string;
    assetType: AssetTypesEnum;
    required?: boolean;
  }): JSX.Element => {
    const isRequired = (label: string) => (required ? markAsRequired(label) : label);
    return (
      <Controller
        control={control}
        name={name}
        rules={{ required }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <FormField>
            <VideoAssetBrowser
              label={isRequired(label)}
              assetId={moduleId || ''}
              assetType={assetType}
              acceptedFileTypes={{ 'video/*': ['.mp4'] }}
              value={value as string}
              data-testid={shortNovelasClipsFormTestIds.formField(name)}
              hasError={!!error}
              onChange={onChange}
            />
          </FormField>
        )}
      />
    );
  };

  return (
    <FormBody data-testid={shortNovelasClipsFormTestIds.formBody}>
      <Box>
        <LokaliseField
          name="copy"
          control={control}
          toTranslate="layouts.short_novelas_clips.fields"
          dataTestId={shortNovelasClipsFormTestIds.formField('copy')}
          required
        />
        <LokaliseField
          name="ctaText"
          control={control}
          toTranslate="layouts.short_novelas_clips.fields"
          dataTestId={shortNovelasClipsFormTestIds.formField('ctaText')}
          required
        />
        <VideoAssetBrowserField
          name="backgroundFillVideo"
          label={t('layouts.short_novelas_clips.fields.backgroundFillVideo')}
          assetType={AssetTypes.shortNovelasClips.backgroundFillVideo}
          required
        />
      </Box>
    </FormBody>
  );
});

ShortNovelasClipsForm.displayName = 'ShortNovelasClipsForm';

export default ShortNovelasClipsForm;
